import { Injectable, Inject } from '@angular/core';
import { UserProfile } from './model/user-profile.model';
import { AuthService } from '@auth0/auth0-angular';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userProfile: UserProfile;
  public isAuthenticated: boolean;
  public idToken?: string;

  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    public authService: AuthService
  ) {
    this.isAuthenticated = false;
    this.userProfile = new UserProfile();
    this.authService.isAuthenticated$.subscribe((data) => {
      this.isAuthenticated = data;
    });

    this.authService.idTokenClaims$.subscribe((data) => {
      this.idToken = data?.__raw;
    });
  }
  public logout(): void {
    localStorage.clear();
    this.authService.logout({
      logoutParams: { returnTo: this._document.location.origin },
    });
    this.logLogOutEvent();
  }
  public loadUserProfile(user: any): void {
    this.logLoginEvent(user);

    const namespace = 'https://rex.cascadedesigns.com/';
    this.userProfile.isEmployee = this.setIsEmployee(user, namespace);
    this.setAppMetaData(user, namespace);
    this.setUserRole(user, namespace);
    this.setIsSalesRep();
    this.setIsDealer();

    this.setUserProfile(user, namespace);
    this.userProfile.initials = this.getInitials();
  }

  private logLoginEvent(user: any) {
    let usr: { [key: string]: any } = {};
    usr['UserProfile'] = user;
  }

  private logLogOutEvent() {
    let usr: { [key: string]: any } = {};
    usr['UserProfile'] = this.userProfile;
  }

  private setUserProfile(user: any, namespace: string): void {
    this.userProfile.userId = this.getValueByKey(user, `${namespace}userId`);

    this.userProfile.username = this.getValueByKey(
      user,
      `${namespace}username`
    );
    if (this.userProfile.isEmployee) {
      this.userProfile.firstName = this.getValueByKey(user, `nickname`);
    } else {
      this.userProfile.firstName = this.getValueByKey(
        user,
        `${namespace}given_name`
      );
    }

    if (this.userProfile.isEmployee) {
      this.userProfile.lastName = '';
    } else {
      this.userProfile.lastName = this.getValueByKey(
        user,
        `${namespace}family_name`
      );
    }
    this.userProfile.email = this.getValueByKey(user, `${namespace}email`);
  }

  private setAppMetaData(user: any, namespace: string): void {
    const appMetaData = this.getValueByKey(user, `${namespace}app_metadata`);
    this.userProfile.branchPlant = appMetaData?.branchPlant;
    this.userProfile.sbuCode = appMetaData?.sbuCode;
    this.userProfile.commissionCode = appMetaData?.commissionCode;
    this.userProfile.companyCode = appMetaData?.companyCode;
    this.userProfile.accountNumber = appMetaData?.accountNumber;
  }

  private setUserRole(user: any, namespace: string): void {
    const value = this.getValueByKey(user, `${namespace}role`);
    this.userProfile.role = value;
  }

  private setIsEmployee(user: any, namespace: string): boolean {
    let isEmployee: boolean = false;
    const value = this.getValueByKey(user, `${namespace}role`);
    isEmployee = value.toLowerCase() == 'cascade employee' ? true : false;
    return isEmployee;
  }

  private getValueByKey(user: any, key: string): any {
    let value: any = null;
    Object.keys(user).forEach((propertyName) => {
      if (propertyName == key) {
        value = user[propertyName];
      }
    });
    return value;
  }

  private getInitials(): string {
    let initials: string = '';
    try {
      initials = `${this.userProfile.firstName?.substring(
        0,
        1
      )}${this.userProfile.lastName?.substring(0, 1)}`;
    } catch (error) {
      initials = 'UU';
    }
    return initials;
  }

  private setIsSalesRep(): void {
    if (this.userProfile.commissionCode) {
      if (this.isNumber(this.userProfile.commissionCode)) {
        if (this.userProfile.commissionCode > 0) {
          this.userProfile.isSalesRep = true;
          this.userProfile.isDealer = false;
        }
      }
    }
  }

  private setIsDealer(): void {
    if (this.userProfile.accountNumber) {
      if (this.isNumber(this.userProfile.accountNumber)) {
        if (this.userProfile.accountNumber > 0) {
          this.userProfile.isSalesRep = false;
          this.userProfile.isDealer = true;
        }
      }
    }
  }

  private isNumber(number: any): boolean {
    try {
      Number.parseInt(number);
      return true;
    } catch (error) {
      return false;
    }
  }
}
